import { Box } from '@mui/material';
import { HrBotContext } from 'components/@home/drawers/HrBotDrawer/hrBotContext';
import extractionFileMatchesForSend, {
  getExtractionRoutingDescriptor,
} from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import DropzoneBox from 'components/common/DropzoneBox';
import Loading from 'components/common/Loading';
import isEmpty from 'lodash/isEmpty';
import { bool, func, object, string } from 'prop-types';
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import authUser from 'store/selectors/authUser';
import { I18n } from 'utils/i18n';
import getPdfjs from 'utils/pdfjs';
import { useDebounceCallback } from 'utils/useDebounceCallback';
import useEventCallback from 'utils/useEffectEvent';
import DroppedFiles from './DroppedFiles';
import { getFileName, getParts } from './pdf-utils';

const readFile = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsString = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.readAsText(file, 'utf8');
  });
};

const StepFiles = ({
  error,
  nextEnabled,
  resets,
  setError,
  setNextEnabled,
  setParsingPct,
  userItems,
}) => {
  const user = useSelector(authUser);
  const { files, format, setFiles, tab, checkIds, droppedFiles, setDroppedFiles } =
    useContext(HrBotContext);
  const [loading, setLoading] = useState(false);
  const parseFailed = useRef(false);
  const parseFailedAt = useRef(0);

  const parseFiles = useEventCallback((newFiles = Object.values(droppedFiles || {})) => {
    if (loading || !newFiles?.length) return;
    parseFailed.current = false;
    setLoading(true);
    const initResets = resets.current;
    if (isEmpty(newFiles)) return;
    const parsed = newFiles.map(() => 0);
    let hasToReset = true;
    setError(null);
    const field = format.sendBy || 'code';
    Promise.all(
      newFiles
        .filter(f => format.type !== 'xml' || f?.name.toLowerCase().endsWith('.xml'))
        .map(async (file, idx) => {
          if (resets.current !== initResets) {
            setParsingPct(0);
            return { code: new Set(), id: new Set() };
          }
          let xml;
          let pdf;
          let pdfLoadingTask = null;
          if (format.type === 'xml') {
            xml = await readFileAsString(file);
          } else {
            const arrayBuffer = await readFile(file);
            const fileContent = new Uint8Array(arrayBuffer);
            const pdfjs = await getPdfjs();
            pdfLoadingTask = pdfjs.getDocument({ data: fileContent });
            pdf = await pdfLoadingTask.promise;
          }
          const routeSets = { code: new Set(), id: new Set() };
          const fileMap = { xml, 'single-pdf': pdf, 'multi-pdf': pdf };
          await getParts(file.name, fileMap[format.type], format, async (parts, pageIdx) => {
            const {
              field: sendField,
              routedByDni,
              routeCheckFormat,
              routeCheckKey,
            } = getExtractionRoutingDescriptor(field, parts);
            if (sendField === 'id' && Object.keys(parts).includes('code')) {
              delete parts.code;
            }
            if (routeCheckKey) {
              routeSets[routeCheckFormat].add(routeCheckKey);
            }
            const rowHasEmptyExtractedField = Object.entries(parts)
              .filter(([key]) => {
                if (key === 'code' && sendField === 'id') return false;
                if (key === 'code' && routedByDni) return false;
                return true;
              })
              .some(([, value]) => value === '');
            const err = rowHasEmptyExtractedField || !routeCheckKey;
            if (resets.current !== initResets) {
              setParsingPct(0);
              return;
            }
            await setFiles(oldValue => [
              ...(hasToReset ? [] : oldValue || []),
              {
                ...parts,
                file: newFiles[parts.filename] || file,
                xml,
                pageNum: pageIdx,
                newFileName: getFileName({ ...parts, ...userItems }, format),
                error: err,
                routedByDni,
                routeCheckFormat,
                routeCheckKey,
                employee: {},
              },
            ]);
            hasToReset = false;
            const total = format?.type === 'single-pdf' ? pdf.numPages : 1;
            parsed[idx] = Math.max(parsed[idx], ((pageIdx + 1) / total) * 100);
            setParsingPct(Math.round(parsed.reduce((acc, curr) => acc + curr, 0) / parsed.length));
          });
          pdfLoadingTask?.destroy();
          pdfLoadingTask?._worker.destroy();
          return routeSets;
        }),
    )
      .then(setsArr => {
        const merged = { code: new Set(), id: new Set() };
        for (const s of setsArr) {
          s.code.forEach(x => merged.code.add(x));
          s.id.forEach(x => merged.id.add(x));
        }
        return Promise.all([
          merged.code.size
            ? checkIds({ ids: [...merged.code], format: 'code' })
            : Promise.resolve({ value: { existingEmployees: {} } }),
          merged.id.size
            ? checkIds({ ids: [...merged.id], format: 'id' })
            : Promise.resolve({ value: { existingEmployees: {} } }),
        ]).then(([codeRes, idRes]) => ({
          existingEmployeesCode: codeRes.value?.existingEmployees || {},
          existingEmployeesId: idRes.value?.existingEmployees || {},
        }));
      })
      .then(({ existingEmployeesCode, existingEmployeesId }) => {
        const hasCodeMatches = Object.keys(existingEmployeesCode || {}).length > 0;
        const hasIdMatches = Object.keys(existingEmployeesId || {}).length > 0;
        if (hasCodeMatches || hasIdMatches) {
          setFiles(oldValue => {
            return oldValue.map(f => {
              const map = f.routeCheckFormat === 'id' ? existingEmployeesId : existingEmployeesCode;
              const existingEmployee = map[f.routeCheckKey];
              return {
                ...f,
                employee: { ...f.employee, ...existingEmployee },
              };
            });
          });
        }
        setLoading(false);
      })
      .catch(() => {
        if (resets.current !== initResets) return;
        parseFailed.current = true;
        parseFailedAt.current = resets.current;
        setError(I18n.t('HrBot.Error parsing some files'));
        setParsingPct(0);
        setLoading(false);
      });
  });

  const handleDrop = useCallback(
    newFiles => {
      setDroppedFiles(newFiles.reduce((acc, f) => ({ ...acc, [f.name]: f }), {}));
      parseFiles(newFiles);
    },
    [setDroppedFiles, parseFiles],
  );

  useEffect(() => {
    if (format) {
      parseFiles();
    }
  }, [format, parseFiles]);

  useEffect(() => {
    if (nextEnabled && (error || loading)) {
      setNextEnabled(false);
    } else if (!nextEnabled && !error && !loading) {
      setNextEnabled(true);
    }
  }, [error, loading, nextEnabled, setNextEnabled]);

  const setErrors = useCallback(() => {
    if (parseFailed.current && resets.current !== parseFailedAt.current) {
      parseFailed.current = false;
    }
    if (parseFailed.current) {
      setError(I18n.t('HrBot.Error parsing some files'));
      return;
    }
    const hasErrors = files?.filter(f => f.error).length > 0;
    const hasRecipients =
      files?.filter(f => extractionFileMatchesForSend(f, format.sendBy || 'code')).length > 0;

    if (hasErrors && !loading) {
      setError(I18n.t('HrBot.Error parsing some files'));
    } else if (files?.length && !hasRecipients && !loading) {
      setError(I18n.t('HrBot.No recipients found'));
    } else {
      setError(null);
    }
  }, [files, format.sendBy, loading, resets, setError]);

  const setErrorsDebounced = useDebounceCallback(setErrors, 300);
  useEffect(setErrorsDebounced, [setErrorsDebounced, files, format, setError, setNextEnabled]);

  const accept = {
    pdf: 'application/pdf',
    xml: 'application/pdf, application/xml, text/xml',
  };
  return (
    <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', mx: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box
            sx={{
              flexGrow: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loading debounce={20} size={64} />
          </Box>
        )}
        {!loading &&
          format &&
          (files ? (
            <DroppedFiles tab={tab} files={files} isSuperAdmin={user?.isSuperAdmin} />
          ) : (
            <DropzoneBox
              onDrop={handleDrop}
              accept={accept[format.type] || 'application/pdf'}
              multiple
            />
          ))}
      </Box>
    </Box>
  );
};

StepFiles.propTypes = {
  nextEnabled: bool.isRequired,
  resets: object.isRequired,
  setError: func.isRequired,
  setNextEnabled: func.isRequired,
  setParsingPct: func.isRequired,
  userItems: object.isRequired,
  error: string.isRequired,
};

export default memo(StepFiles);
